/**
 * AppliedPromosSection Component
 * Displays applied promos and vouchers with their savings
 */

import { Box, Flex, Stack } from '@chakra-ui/react';
import type { CartTotalWithMagnify } from '@diamond/shared/types';
import { Text } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import { memo, useMemo } from 'react';

export interface AppliedPromosSectionProps {
  cartTotalData: CartTotalWithMagnify | undefined;
}

interface PromoItem {
  title: string;
  savings: number;
}

type DiscountPromo = { deduction_amount?: number };
type FreeGoodsPromo = { quantity_free_goods?: number | null; title?: string };
type AppliedVoucher = {
  voucher_title?: string;
  voucher_code?: string;
  product_discounts?: Array<{ discount_amount: number }>;
  free_goods?: Array<{ quantity: number; product_name: string }>;
};

const processVouchers = (
  vouchers: AppliedVoucher[] | undefined,
  result: PromoItem[]
) => {
  // Defensive check: ensure vouchers is a valid array
  if (!vouchers || !Array.isArray(vouchers)) {
    return;
  }

  vouchers.forEach((voucher) => {
    // Defensive check: skip invalid voucher objects
    if (!voucher || typeof voucher !== 'object') {
      return;
    }

    const voucherSavings = Array.isArray(voucher.product_discounts)
      ? voucher.product_discounts.reduce(
          (total, discount) =>
            total +
            (typeof discount?.discount_amount === 'number'
              ? discount.discount_amount
              : 0),
          0
        )
      : 0;

    let title = voucher.voucher_title || voucher.voucher_code || '';

    if (Array.isArray(voucher.free_goods) && voucher.free_goods.length > 0) {
      const freeGood = voucher.free_goods[0];
      if (freeGood) {
        title += ` • GRATIS ${freeGood.quantity || 0} ${
          freeGood.product_name || ''
        }`;
      }
    }

    if (voucherSavings > 0) {
      result.push({ title, savings: voucherSavings });
    }
  });
};

const processDiscountPromos = (
  promos: DiscountPromo[] | undefined,
  title: string,
  result: PromoItem[]
) => {
  // Defensive check: ensure promos is a valid array
  if (!promos || !Array.isArray(promos)) {
    return;
  }

  promos.forEach((promo) => {
    // Defensive check: skip invalid promo objects
    if (!promo || typeof promo !== 'object') {
      return;
    }
    if (
      typeof promo.deduction_amount === 'number' &&
      promo.deduction_amount > 0
    ) {
      result.push({ title, savings: promo.deduction_amount });
    }
  });
};

const processFreeGoodsPromos = (
  promos: FreeGoodsPromo[] | undefined,
  result: PromoItem[]
) => {
  // Defensive check: ensure promos is a valid array
  if (!promos || !Array.isArray(promos)) {
    return;
  }

  promos.forEach((promo) => {
    // Defensive check: skip invalid promo objects
    if (!promo || typeof promo !== 'object') {
      return;
    }
    result.push({
      title: `GRATIS ${
        typeof promo.quantity_free_goods === 'number'
          ? promo.quantity_free_goods / 1000
          : 0
      } ${promo.title || ''}`,
      savings: 0,
    });
  });
};

function AppliedPromosSectionInner({
  cartTotalData,
}: AppliedPromosSectionProps) {
  const appliedPromosAndVouchers = useMemo(() => {
    if (!cartTotalData || typeof cartTotalData !== 'object') return [];

    const result: PromoItem[] = [];

    processVouchers(cartTotalData.vouchers?.applied_vouchers, result);

    // Defensive check: ensure promos is a valid array before iterating
    if (
      Array.isArray(cartTotalData.promos) &&
      cartTotalData.promos.length > 0
    ) {
      cartTotalData.promos.forEach((promoDivision) => {
        // Defensive check: skip invalid promoDivision objects
        if (!promoDivision || typeof promoDivision !== 'object') {
          return;
        }
        processDiscountPromos(
          promoDivision.single_discount_promos,
          'Promo Diskon',
          result
        );
        processDiscountPromos(
          promoDivision.bundled_discount_promos,
          'Promo Bundling Diskon',
          result
        );
        processFreeGoodsPromos(promoDivision.single_free_good_promos, result);
        processFreeGoodsPromos(promoDivision.bundled_free_good_promos, result);
      });
    }

    return result;
  }, [cartTotalData]);

  if (appliedPromosAndVouchers.length === 0) {
    return null;
  }

  // TODO: Commented out for later - Promo items display
  // Uncomment the return statement below when ready to show promo items
  return null;

  /* return (
    <Box p={5}>
      <Stack spacing={3}>
        {appliedPromosAndVouchers.map((item, idx) => (
          <Flex
            key={`promo-${idx}`}
            justify="space-between"
            align="center"
            bg="green.50"
            p={3}
            borderRadius="md"
            border="1px"
            borderColor="green.200"
          >
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                {item.title}
              </Text>
            </Box>
            {item.savings > 0 && (
              <Text fontSize="sm" fontWeight="bold" color="red">
                Hemat {buyerCurrencyFormatter(item.savings)}
              </Text>
            )}
          </Flex>
        ))}
      </Stack>
    </Box>
  ); */
}

export const AppliedPromosSection = memo(AppliedPromosSectionInner);
